import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { BilingualName, CompanyPlantType, Currency, Errors, InterestRateMaster, LatestCurrency } from '../../contexts/types';
import { CustomNumberInput } from '../InvestmentScreening/CustomNumberInput';
import { formatLocalDateTime } from '../../utils/utils';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';

/** 金利マスタ地域選択肢（ユニークな地域コードと地域名） */
interface AreaOption {
  areaCode: string;
  areaName: BilingualName;
}

/** 編集モーダルのProps */
interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: CompanyPlantType | null;
  parentOptions: CompanyPlantType[];
  currencyData: Currency[];
  areaOptions: AreaOption[];
  onSaveSuccess: () => void;
}

/** 項目（ラベル）スタイル */
const labelStyle = {
  fontWeight: "bold",
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  marginTop: "8px",
  width: 170,
}

/** 項目（値）幅 */
const valueWidth = 475;

/**
 * 会社/工場編集モーダル（独立コンポーネント）
 * 親コンポーネントの再レンダリングを防ぐため分離
 */
const CompanyPlantEditModal: React.FC<EditModalProps> = React.memo(({
  isOpen,
  onClose,
  initialData,
  parentOptions,
  currencyData,
  areaOptions,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);
  const TITLE = t('features.MasterMaintenance.CompanyPlant.title');

  /** 編集対象データ（モーダル内部状態） */
  const [editTarget, setEditTarget] = useState<CompanyPlantType>({
    companyPlantId: "",
    companyPlantName: "",
    parentCompanyPlantId: null,
  });

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** 保存中フラグ */
  const [isSaving, setIsSaving] = useState(false);

  /** モーダルが開いたときに初期データをセット */
  useEffect(() => {
    if (isOpen && initialData) {
      setEditTarget(initialData);
      setErrors({});
    } else if (isOpen && !initialData) {
      // 新規登録
      setEditTarget({
        companyPlantId: "",
        companyPlantName: "",
        parentCompanyPlantId: null,
      });
      setErrors({});
    }
  }, [isOpen, initialData]);

  /** プロパティ値の変更 */
  const changeEditTarget = useCallback((propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }, []);

  /** バリデーション */
  const validate = useCallback(() => {
    const localErrors: Errors = {};
    const required = (value?: string | number | null) => Boolean(value || value === 0);

    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string) => {
      const requiredResult = required(target[propertyName]);
      if (!requiredResult) {
        localErrors[propertyName] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    };

    let result = true;

    if (!requiredAndSetError(editTarget, "companyPlantId", t('features.MasterMaintenance.CompanyPlant.field.id'))) result = false;
    if (!requiredAndSetError(editTarget, "companyPlantName", t('features.MasterMaintenance.CompanyPlant.field.name'))) result = false;
    if (!requiredAndSetError(editTarget, "companyPlantNameEn", t('features.MasterMaintenance.CompanyPlant.field.nameEn'))) result = false;
    if (!requiredAndSetError(editTarget, "companyCode", t('features.MasterMaintenance.CompanyPlant.field.companyCode'))) result = false;
    if (!requiredAndSetError(editTarget, "currencyId", t('features.MasterMaintenance.CompanyPlant.field.currency'))) result = false;
    if (!requiredAndSetError(editTarget, "locationCountry", t('features.MasterMaintenance.CompanyPlant.field.locationCountry'))) result = false;
    if (!requiredAndSetError(editTarget, "areaCode", t('features.MasterMaintenance.CompanyPlant.field.areaCode'))) result = false;
    if (!requiredAndSetError(editTarget, "taxRate", t('features.MasterMaintenance.CompanyPlant.field.taxRate'))) result = false;
    if (!requiredAndSetError(editTarget, "order", t('common.label.sortOrder'))) result = false;

    setErrors(localErrors);
    return result;
  }, [editTarget]);

  /** 保存処理 */
  const handleSave = useCallback(async () => {
    if (!validate()) {
      toast({
        title: TITLE,
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      const input = {
        companyPlantId: editTarget.companyPlantId,
        companyPlantName: editTarget.companyPlantName,
        companyPlantNameEn: editTarget.companyPlantNameEn || "",
        parentCompanyPlantId: editTarget.parentCompanyPlantId || null,
        companyCode: editTarget.companyCode || "",
        currencyId: editTarget.currencyId || "",
        locationCountry: editTarget.locationCountry || "",
        areaCode: editTarget.areaCode || "",
        taxRate: editTarget.taxRate ?? 0,
        order: editTarget.order ?? 0,
        deleted: editTarget.deleted || false,
        updateDate: editTarget.updateDate,
      };

      const result = await WebAPI.request({
        query: `
          mutation putCompanyPlant($input: CompanyPlantInput!) {
            putCompanyPlant(input: $input) {status}
          }
        `,
        variables: { input },
      });

      if (result.data.putCompanyPlant.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onSaveSuccess();
        onClose();
      } else if (result?.data?.putCompanyPlant.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Exception to call API putCompanyPlant. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [editTarget, validate, WebAPI, toast, onSaveSuccess, onClose, t, TITLE]);

  /** 閉じるボタン */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  /** モーダルが閉じた後の初期化 */
  const handleCloseComplete = useCallback(() => {
    setEditTarget({
      companyPlantId: "",
      companyPlantName: "",
      parentCompanyPlantId: null,
    });
    setErrors({});
  }, []);

  /** 親会社選択肢（自分自身を除外） */
  const filteredParentOptions = useMemo(() => {
    return parentOptions.filter(m => m.companyPlantId !== editTarget.companyPlantId);
  }, [parentOptions, editTarget.companyPlantId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t('features.MasterMaintenance.CompanyPlant.modal.editTitle')}</Text>
        </ModalHeader>
        <ModalBody>
          <VStack>
            <FormControl isInvalid={errors.companyPlantId?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.id')}</FormLabel>
                <VStack align="left">
                  <Input
                    value={editTarget.companyPlantId || ""}
                    onChange={(e) => changeEditTarget("companyPlantId", e.target.value)}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.idExample')}
                    width={valueWidth}
                    maxLength={InputLimits.COMPANY_PLANT_ID}
                    isInvalid={errors.companyPlantId?.isError}
                    isDisabled={!!editTarget.updateDate}
                  />
                  <FormErrorMessage mt={0}>{errors.companyPlantId?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.companyPlantName?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.name')}</FormLabel>
                <VStack align="left">
                  <Input
                    value={editTarget.companyPlantName || ""}
                    onChange={(e) => changeEditTarget("companyPlantName", e.target.value)}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.name')}
                    width={valueWidth}
                    maxLength={InputLimits.COMPANY_PLANT_NAME}
                    isInvalid={errors.companyPlantName?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.companyPlantName?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.companyPlantNameEn?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.nameEn')}</FormLabel>
                <VStack align="left">
                  <Input
                    value={editTarget.companyPlantNameEn || ""}
                    onChange={(e) => changeEditTarget("companyPlantNameEn", e.target.value)}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.nameEn')}
                    width={valueWidth}
                    maxLength={InputLimits.COMPANY_PLANT_NAME}
                    isInvalid={errors.companyPlantNameEn?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.companyPlantNameEn?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.companyCode?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.companyCode')}</FormLabel>
                <VStack align="left">
                  <Input
                    value={editTarget.companyCode || ""}
                    onChange={(e) => changeEditTarget("companyCode", e.target.value)}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.companyCodeExample')}
                    width={valueWidth}
                    maxLength={InputLimits.COMPANY_CODE}
                    isInvalid={errors.companyCode?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.companyCode?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.parent')}</FormLabel>
                <VStack align="left">
                  <Select
                    value={editTarget.parentCompanyPlantId || ""}
                    onChange={(e) => changeEditTarget("parentCompanyPlantId", e.target.value || null)}
                    width={valueWidth}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.parentEmpty')}
                  >
                    {filteredParentOptions.map(option => (
                      <option key={option.companyPlantId} value={option.companyPlantId}>
                        {option.companyPlantId} - {option.companyPlantName}
                      </option>
                    ))}
                  </Select>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.currencyId?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.currency')}</FormLabel>
                <VStack align="left">
                  <Select
                    value={editTarget.currencyId || ""}
                    onChange={(e) => changeEditTarget("currencyId", e.target.value)}
                    width={valueWidth}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.currency')}
                    isInvalid={errors.currencyId?.isError}
                  >
                    {currencyData.map(currency => (
                      <option key={currency.currencyId} value={currency.currencyId}>
                        {currency.currencyName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage mt={0}>{errors.currencyId?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.locationCountry?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.locationCountry')}</FormLabel>
                <VStack align="left">
                  <Input
                    value={editTarget.locationCountry || ""}
                    onChange={(e) => changeEditTarget("locationCountry", e.target.value)}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.locationCountryExample')}
                    width={valueWidth}
                    maxLength={InputLimits.LOCATION_COUNTRY}
                    isInvalid={errors.locationCountry?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.locationCountry?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.areaCode?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.areaCode')}</FormLabel>
                <VStack align="left">
                  <Select
                    value={editTarget.areaCode || ""}
                    onChange={(e) => changeEditTarget("areaCode", e.target.value)}
                    width={valueWidth}
                    placeholder={t('features.MasterMaintenance.CompanyPlant.placeholder.areaCode')}
                    isInvalid={errors.areaCode?.isError}
                  >
                    {areaOptions.map(area => (
                      <option key={area.areaCode} value={area.areaCode}>
                        {area.areaCode} - {getMultilingualDisplayName(area.areaName)}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage mt={0}>{errors.areaCode?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.taxRate?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('features.MasterMaintenance.CompanyPlant.field.taxRate')}</FormLabel>
                <VStack align="left">
                  <CustomNumberInput
                    value={editTarget.taxRate || 0}
                    onChange={(value) => changeEditTarget("taxRate", value)}
                    placeholder="%"
                    min={0}
                    max={100}
                    decimalPlaces={2}
                    w={valueWidth}
                    isInvalid={errors.taxRate?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.taxRate?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl isInvalid={errors.order?.isError} h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('common.label.sortOrder')}</FormLabel>
                <VStack align="left">
                  <Input
                    type="number"
                    value={editTarget.order ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      changeEditTarget("order", val === "" ? null : parseInt(val, 10));
                    }}
                    placeholder={t('common.label.sortOrder')}
                    min={0}
                    max={9999}
                    width={valueWidth}
                    isInvalid={errors.order?.isError}
                  />
                  <FormErrorMessage mt={0}>{errors.order?.errorMessage}</FormErrorMessage>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl h={70}>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('common.label.deleteFlag')}</FormLabel>
                <VStack align="left">
                  <HStack>
                    <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                      onClick={() => changeEditTarget("deleted", false)}>{t('common.label.notDeleted')}</Button>
                    <Switch
                      isChecked={editTarget.deleted}
                      onChange={(e) => changeEditTarget("deleted", e.target.checked)}
                    />
                    <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                      onClick={() => changeEditTarget("deleted", true)}>{t('common.label.deleted')}</Button>
                  </HStack>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('common.label.createDate')}</FormLabel>
                <VStack align="left">
                  <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                </VStack>
              </Flex>
            </FormControl>

            <FormControl>
              <Flex align="start">
                <FormLabel style={labelStyle}>{t('common.label.updateDate')}</FormLabel>
                <VStack align="left">
                  <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                </VStack>
              </Flex>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
            e.currentTarget.blur();
            handleSave();
          }} m={1}>
            {t('common.button.save')}
          </Button>
          <Button onClick={handleClose} m={1}>
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

/**
 * マスタメンテナンス - 会社/工場 画面
 */
const CompanyPlant: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.CompanyPlant.title');
  /** エンティティ略称 */
  const ENTITY = t('features.MasterMaintenance.CompanyPlant.entityLabel');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "normal",
    wordBreak: "break-word" as const,
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル - 1列目（会社/工場ID） */
  const fixedCol1Style = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
  }

  /** 列固定スタイル - 2列目（会社/工場名） */
  const fixedCol2Style = {
    position: "sticky" as const,
    left: "180px",
    zIndex: "1",
    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
  }

  /** ヘッダー固定列用背景色 */
  const fixedHeaderBgColor = "gray.100";

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 会社/工場マスタデータ */
  const [masterData, setMasterData] = useState<CompanyPlantType[]>([]);

  /** 通貨マスタデータ */
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);

  /** 金利マスタ地域選択肢 */
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);

  /** 編集モーダルに渡す初期データ（新規の場合はnull） */
  const [editInitialData, setEditInitialData] = useState<CompanyPlantType | null>(null);

  /** モーダル表示制御用：編集用モーダル */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）会社/工場IDを管理
  const [filterCompanyPlantId, setFilterCompanyPlantId] = useState<string>("");

  // （フィルター）会社/工場名を管理
  const [filterCompanyPlantName, setFilterCompanyPlantName] = useState<string>("");

  // （フィルター）会社コードを管理
  const [filterCompanyCode, setFilterCompanyCode] = useState<string>("");

  /** フィルター済みマスタデータ（メモ化） */
  const filteredMasterData = useMemo(() => {
    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    return (masterData ?? []).filter(m => {
      if (!m) {
        return false;
      }

      return (
        hasFilterString(m.companyPlantId, filterCompanyPlantId) &&
        hasFilterString(m.companyPlantName, filterCompanyPlantName) &&
        hasFilterString(m.companyCode, filterCompanyCode)
      );
    });
  }, [masterData, filterCompanyPlantId, filterCompanyPlantName, filterCompanyCode]);

  /** 親IDの選択肢（会社のみ = parentCompanyPlantIdがnullのもの）（メモ化） */
  const parentOptions = useMemo(() => {
    return (masterData ?? []).filter(m => !m.parentCompanyPlantId);
  }, [masterData]);

  /** 通貨名を取得 */
  const getCurrencyName = useCallback((currencyId?: string) => {
    if (!currencyId) return "";
    const currency = currencyData.find(c => c.currencyId === currencyId);
    return currency?.currencyName || currencyId;
  }, [currencyData]);

  /** 親会社名を取得 */
  const getParentName = useCallback((parentCompanyPlantId?: string | null) => {
    if (!parentCompanyPlantId) return "";
    const parent = masterData.find(m => m.companyPlantId === parentCompanyPlantId);
    return parent?.companyPlantName || parentCompanyPlantId;
  }, [masterData]);

  /** 地域名を取得 */
  const getAreaName = useCallback((areaCode?: string) => {
    if (!areaCode) return "";
    const area = areaOptions.find(a => a.areaCode === areaCode);
    return (area && getMultilingualDisplayName(area.areaName)) || areaCode;
  }, [areaOptions]);

  /**
   * 画面初期表示
   */
  useEffect(() => {
    fetchMasterData();
    fetchCurrencyData();
    fetchAreaOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = useCallback(() => {
    setEditInitialData(null);
    onOpenEditModal();
  }, [onOpenEditModal]);

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query getCompanyPlant($hasDeleted: Boolean) {
            getCompanyPlant(hasDeleted: $hasDeleted) {
              companyPlantId
              companyPlantName
              companyPlantNameEn
              parentCompanyPlantId
              companyCode
              currencyId
              locationCountry
              areaCode
              baseInterestRate
              taxRate
              order
              deleted
              createDate
              updateDate
            }
          }`,
        variables: {
          hasDeleted: true,
        }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getCompanyPlant) {
        // 会社/工場マスタ情報（階層を維持しながら表示順でソート）
        const data = response.data.getCompanyPlant as CompanyPlantType[];

        // 親（会社）と子（工場）を分離
        const parents = data.filter((item: CompanyPlantType) => !item.parentCompanyPlantId);
        const children = data.filter((item: CompanyPlantType) => !!item.parentCompanyPlantId);

        // 親を表示順でソート
        parents.sort((a: CompanyPlantType, b: CompanyPlantType) => (a.order ?? 0) - (b.order ?? 0));

        // 階層構造を維持したリストを構築
        const sortedData: CompanyPlantType[] = [];
        parents.forEach((parent: CompanyPlantType) => {
          sortedData.push(parent);
          // この親に属する子を表示順でソートして追加
          const childrenOfParent = children
            .filter((child: CompanyPlantType) => child.parentCompanyPlantId === parent.companyPlantId)
            .sort((a: CompanyPlantType, b: CompanyPlantType) => (a.order ?? 0) - (b.order ?? 0));
          sortedData.push(...childrenOfParent);
        });

        setMasterData(sortedData);
      } else {
        console.error(`Failed to call API getCompanyPlant. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchMasterData", error);
      toast({
        title: TITLE,
        description: `会社/工場一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 通貨データ取得
   */
  const fetchCurrencyData = async () => {
    try {
      const response = await WebAPI.request({
        query: `
        query {
          getLatestCurrencyFromSas {
            currencyId
            currencyName
            exchangeRate
            rateMonth
          }
        }`,
      });
      if (response?.data?.getLatestCurrencyFromSas) {
        const data: Currency[] = response.data.getLatestCurrencyFromSas.map(
          (item: LatestCurrency, index: number) => ({
            currencyId: item.currencyId,
            currencyName: item.currencyName,
            exchangeRate: item.exchangeRate,
            order: index + 1,
          })
        );
        setCurrencyData(data);
      }

    } catch (error) {
      console.error("Failed. fetchCurrencyData", error);
    }
  }

  /**
   * 金利マスタからユニークな地域コード一覧を取得
   */
  const fetchAreaOptions = async () => {
    try {
      const payload = {
        query: `
          query getInterestRateMaster($hasDeleted: Boolean) {
            getInterestRateMaster(hasDeleted: $hasDeleted) {
              areaCode
              areaName { ja en }
              order
              deleted
            }
          }`,
        variables: {
          hasDeleted: false,
        }
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getInterestRateMaster) {
        const data = response.data.getInterestRateMaster as InterestRateMaster[];
        // ユニークな地域コードを抽出（orderでソート）
        const uniqueAreas = new Map<string, AreaOption>();
        data
          .filter(item => !item.deleted && item.areaCode)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .forEach(item => {
            if (item.areaCode && !uniqueAreas.has(item.areaCode)) {
              uniqueAreas.set(item.areaCode, {
                areaCode: item.areaCode,
                areaName: item.areaName || {},
              });
            }
          });
        setAreaOptions(Array.from(uniqueAreas.values()));
      }

    } catch (error) {
      console.error("Failed. fetchAreaOptions", error);
    }
  }

  /**
   * 「会社/工場ID」リンク押下
   * @param companyPlantId 会社/工場ID
   */
  const handleEdit = async (companyPlantId?: string | null) => {
    try {
      const payload = {
        query: `
          query getCompanyPlantById($companyPlantId: String!) {
            getCompanyPlantById(companyPlantId: $companyPlantId) {
              companyPlantId
              companyPlantName
              companyPlantNameEn
              parentCompanyPlantId
              companyCode
              currencyId
              locationCountry
              areaCode
              baseInterestRate
              taxRate
              order
              deleted
              createDate
              updateDate
            }
          }`,
        variables: {
          companyPlantId: companyPlantId,
        }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getCompanyPlantById) {
        // 会社/工場マスタ情報をモーダルに渡す
        setEditInitialData(response?.data?.getCompanyPlantById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getCompanyPlantById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. handleEdit", error);
      toast({
        title: TITLE,
        description: `会社/工場情報取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 保存成功時のコールバック
   */
  const handleSaveSuccess = useCallback(() => {
    fetchMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <Flex gap={4} align="center">
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>
        </Flex>
        <TableContainer overflowX="auto" overflowY="auto" w="100%" maxW="2400px" h="calc(100dvh - 130px)" mt={4}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={180} />
              <col width={300} />
              <col width={300} />
              <col width={150} />
              <col width={300} />
              <col width={100} />
              <col width={150} />
              <col width={180} />
              <col width={100} />
              <col width={100} />
              <col width={100} />
              <col width={200} />
              <col width={200} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedCol1Style} zIndex={3} backgroundColor={fixedHeaderBgColor}>
                  {t('features.MasterMaintenance.CompanyPlant.field.id')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterCompanyPlantId} setFilterValue={setFilterCompanyPlantId} />
                </Th>
                <Th {...headerCellStyle} {...fixedCol2Style} zIndex={3} backgroundColor={fixedHeaderBgColor}>
                  {t('features.MasterMaintenance.CompanyPlant.field.name')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterCompanyPlantName} setFilterValue={setFilterCompanyPlantName} />
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.CompanyPlant.field.nameEn')}
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.CompanyPlant.field.companyCode')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterCompanyCode} setFilterValue={setFilterCompanyCode} />
                </Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CompanyPlant.field.parent')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CompanyPlant.field.currency')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CompanyPlant.field.locationCountry')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CompanyPlant.field.areaCode')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.CompanyPlant.field.taxRate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.createDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : "white";
                const isChild = !!record.parentCompanyPlantId;

                return (
                  <Tr role="group" key={record.companyPlantId} _hover={{ bg: "gray.200" }}>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} {...fixedCol1Style} bgColor={bgColor} pl={isChild ? 8 : 4}>
                      <Button
                        variant="link"
                        textDecoration="underline"
                        color="blue.500"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          handleEdit(record.companyPlantId)
                        }}
                        sx={{
                          display: 'inline-block',
                          maxW: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {isChild ? "└ " : ""}{record.companyPlantId}
                      </Button>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} {...fixedCol2Style} bgColor={bgColor}>
                      <Tooltip label={record.companyPlantName} placement="top" hasArrow isDisabled={!record.companyPlantName || record.companyPlantName.length <= 20}>
                        <Text noOfLines={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {record.companyPlantName}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      <Tooltip label={record.companyPlantNameEn} placement="top" hasArrow isDisabled={!record.companyPlantNameEn || record.companyPlantNameEn.length <= 20}>
                        <Text noOfLines={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {record.companyPlantNameEn}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.companyCode}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      {(() => {
                        const parentName = getParentName(record.parentCompanyPlantId);
                        return (
                          <Tooltip label={parentName} placement="top" hasArrow isDisabled={!parentName || parentName.length <= 20}>
                            <Text noOfLines={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                              {parentName}
                            </Text>
                          </Tooltip>
                        );
                      })()}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{getCurrencyName(record.currencyId)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      <Tooltip label={record.locationCountry} placement="top" hasArrow isDisabled={!record.locationCountry || record.locationCountry.length <= 10}>
                        <Text noOfLines={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {record.locationCountry}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>
                      {(() => {
                        const areaText = record.areaCode ? `${record.areaCode} (${getAreaName(record.areaCode)})` : "";
                        return (
                          <Tooltip label={areaText} placement="top" hasArrow isDisabled={!areaText || areaText.length <= 15}>
                            <Text noOfLines={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                              {areaText}
                            </Text>
                          </Tooltip>
                        );
                      })()}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.taxRate}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.order}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル（独立コンポーネント化でパフォーマンス向上） */}
      <CompanyPlantEditModal
        isOpen={isOpenEditModal}
        onClose={onCloseEditModal}
        initialData={editInitialData}
        parentOptions={parentOptions}
        currencyData={currencyData}
        areaOptions={areaOptions}
        onSaveSuccess={handleSaveSuccess}
      />
    </>
  );
}

export default CompanyPlant;
